// Classify a Telegram/MTProto error into an action for the send loop.

export type ErrorClass =
  | "WAIT_GLOBAL" // FLOOD_WAIT: sleep, retry same target, do NOT report
  | "DEFER_CHAT" // SLOWMODE_WAIT: report deferred with seconds
  | "HARD_STOP" // PEER_FLOOD: stop the job, 24h cooldown, no retries
  | "DEAD" // forbidden/blocked/deactivated peer: skip + deactivate group
  | "FATAL_AUTH" // auth key revoked: stop, force re-login
  | "TRANSIENT" // network/timeout/5xx: backoff + retry
  | "MEDIA" // media/file errors: re-wrap + retry once
  | "UNKNOWN";

export interface Classified {
  cls: ErrorClass;
  code: string;
  seconds?: number;
}

const DEAD = new Set([
  "CHAT_WRITE_FORBIDDEN",
  "USER_IS_BLOCKED",
  "CHANNEL_PRIVATE",
  "CHAT_ADMIN_REQUIRED",
  "USER_BANNED_IN_CHANNEL",
  "USER_DEACTIVATED",
  "PEER_ID_INVALID",
  "INPUT_USER_DEACTIVATED",
  "CHAT_RESTRICTED",
  "USER_IS_BOT",
  "YOU_BLOCKED_USER",
]);

const FATAL_AUTH = new Set([
  "AUTH_KEY_UNREGISTERED",
  "AUTH_KEY_INVALID",
  "SESSION_REVOKED",
  "SESSION_EXPIRED",
  "USER_DEACTIVATED_BAN",
]);

function rawMessage(err: unknown): string {
  const e = err as { errorMessage?: string; message?: string; code?: number };
  return (e?.errorMessage || e?.message || "").toString().toUpperCase();
}

function rawSeconds(err: unknown): number | undefined {
  const e = err as { seconds?: number };
  if (typeof e?.seconds === "number") return e.seconds;
  // fall back to parsing a trailing number from the message (FLOOD_WAIT_42)
  const m = rawMessage(err).match(/_(\d+)$/);
  return m ? parseInt(m[1]!, 10) : undefined;
}

export function classifyError(err: unknown): Classified {
  const msg = rawMessage(err);
  const seconds = rawSeconds(err);

  if (msg.startsWith("FLOOD_WAIT") || msg.startsWith("FLOOD_PREMIUM_WAIT")) {
    return { cls: "WAIT_GLOBAL", code: "FLOOD_WAIT", seconds };
  }
  if (msg.startsWith("SLOWMODE_WAIT")) {
    return { cls: "DEFER_CHAT", code: "SLOWMODE_WAIT", seconds };
  }
  if (msg === "PEER_FLOOD") {
    return { cls: "HARD_STOP", code: "PEER_FLOOD" };
  }
  // exact dead codes
  for (const d of DEAD) {
    if (msg === d || msg.startsWith(d)) return { cls: "DEAD", code: d };
  }
  for (const f of FATAL_AUTH) {
    if (msg === f || msg.startsWith(f)) return { cls: "FATAL_AUTH", code: f };
  }
  if (
    msg.startsWith("MEDIA_") ||
    msg.startsWith("FILE_PART") ||
    msg.startsWith("FILE_REFERENCE") ||
    msg === "PHOTO_INVALID" ||
    msg === "PHOTO_SAVE_FILE_INVALID"
  ) {
    return { cls: "MEDIA", code: msg || "MEDIA" };
  }
  if (
    msg.includes("TIMEOUT") ||
    msg.includes("TIMED_OUT") ||
    msg.includes("NETWORK") ||
    msg.includes("DISCONNECT") ||
    msg.includes("CONNECTION") ||
    (err as { code?: number })?.code === 500 ||
    (err as { code?: number })?.code === 503
  ) {
    return { cls: "TRANSIENT", code: "TRANSIENT" };
  }
  return { cls: "UNKNOWN", code: msg || "UNKNOWN" };
}
